import type { VendorCitationData } from '@/components/ui/citation-chip';
import type { StepState } from '@/components/ui/workspace/steps';
import type {
  Disposition,
  DispositionRow,
  JudgeRun,
  NotAssessed,
  ProductFit,
  ProductInScope,
  ProposeResult,
  RatingCitation,
  RatingCell,
  SensitivityCase,
  VendorIntelligence,
  VendorRating,
} from '@/lib/api/vendor-intelligence';

/**
 * Pure helpers of the Vendor intelligence tab (T-0267). Nothing here
 * computes a fit figure: indexes, coverage, ranks and the sensitivity
 * cases are the server's (@caliber-2/vendor-fit). These only look things
 * up, word them, and follow a Propose request to its end.
 */

export function cellKey(productId: string, moduleId: string): string {
  return `${productId}|${moduleId}`;
}

export function ratingsByCell(e: VendorIntelligence): Map<string, VendorRating> {
  return new Map(e.ratings.map((r) => [cellKey(r.product_id, r.module_id), r]));
}

export function judgeRunsByCell(e: VendorIntelligence): Map<string, JudgeRun> {
  return new Map(e.judge_runs.map((r) => [cellKey(r.product_id, r.module_id), r]));
}

/** The proposed ratings of one product, the column's "Confirm all proposed". */
/** Every proposed rating of the evaluation, grid order (module, then product). */
export function allProposed(e: VendorIntelligence): VendorRating[] {
  const moduleAt = new Map(e.modules.map((m, i) => [m.module_id, i]));
  const productAt = new Map(e.products.map((p, i) => [p.product_id, i]));
  return e.ratings
    .filter(
      (r) => r.status === 'proposed' && moduleAt.has(r.module_id) && productAt.has(r.product_id),
    )
    .sort(
      (a, b) =>
        moduleAt.get(a.module_id)! - moduleAt.get(b.module_id)! ||
        productAt.get(a.product_id)! - productAt.get(b.product_id)!,
    );
}

/** "Strong 20 · Partial 30 · Gap 10 · Unknown 24": the ratings a bulk confirm would count. */
export function ratingMixText(ratings: ReadonlyArray<Pick<VendorRating, 'rating'>>): string {
  const order = ['strong', 'partial', 'gap', 'unknown'] as const;
  const label = { strong: 'Strong', partial: 'Partial', gap: 'Gap', unknown: 'Unknown' };
  return order
    .map((k) => [k, ratings.filter((r) => r.rating === k).length] as const)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => `${label[k]} ${n.toLocaleString('en-US')}`)
    .join(' · ');
}

/** Ids in batches the confirm-many endpoint takes (at most 1,000 each). */
export function inBatches<T>(items: readonly T[], size = 1000): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function proposedIdsOf(e: VendorIntelligence, productId: string): string[] {
  return e.ratings
    .filter((r) => r.product_id === productId && r.status === 'proposed')
    .map((r) => r.id);
}

/** A Propose request the tab is following: when it asked and what it sent to the judge. */
export interface PendingRequest {
  requested_at: string;
  requested: RatingCell[];
}

export function pendingRequestOf(result: ProposeResult): PendingRequest | null {
  return result.requested.length > 0
    ? { requested_at: result.requested_at, requested: result.requested }
    : null;
}

/**
 * The requested cells still waiting for the judge: no rating proposed
 * since the request, and no run that finished since it (a skipped or
 * failed item is done, and says why in the panel). Both times are the
 * database's.
 */
export function waitingCells(e: VendorIntelligence, req: PendingRequest): RatingCell[] {
  const since = Date.parse(req.requested_at);
  const ratings = ratingsByCell(e);
  const runs = judgeRunsByCell(e);
  const inScope = new Set(e.products.map((p) => p.product_id));
  const evaluated = new Set(e.modules.map((m) => m.module_id));
  return req.requested.filter((c) => {
    if (!inScope.has(c.product_id) || !evaluated.has(c.module_id)) return false;
    const key = cellKey(c.product_id, c.module_id);
    const rating = ratings.get(key);
    if (rating && Date.parse(rating.created_at) >= since) return false;
    const run = runs.get(key);
    if (run && run.finished_at && Date.parse(run.started_at) >= since) return false;
    return true;
  });
}

export const SOURCE_LABEL: Record<VendorRating['source'], string> = {
  vendor_answers: "From the vendor's own requirement answers",
  knowledge_items: 'Proposed by AI from the knowledge base',
  consultant: 'Changed by a consultant',
};

/** Why the judge did not propose a rating, in plain words. */
export function judgeReasonText(reason: string | null): string {
  switch (reason) {
    case 'budget_exhausted':
      return "The engagement's AI budget is used up, so no rating was proposed.";
    case 'module_off':
      return 'Selection is turned off for this engagement.';
    case 'product_not_in_scope':
      return 'The product was removed from scope before the AI reached it.';
    case 'module_not_evaluated':
      return 'This module no longer holds a requirement that carries weight.';
    case 'already_confirmed':
      return 'A consultant confirmed this rating first, so the AI left it alone.';
    case 'invalid_output':
      return "The AI's answer could not be used twice. Propose ratings again.";
    case 'rate_limited':
    case 'timeout':
    case 'model_error':
      return 'The AI service did not answer. Propose ratings again in a few minutes.';
    default:
      return reason ? `The AI did not propose a rating (${reason}).` : 'No rating yet.';
  }
}

/**
 * A rating's citation in the form the knowledge base's citation view lays
 * out. The answer code is shown beside it, not inside the quote.
 */
export function toVendorCitation(c: RatingCitation): VendorCitationData {
  return {
    text: c.label,
    parts: {
      ...(c.number ? { number: c.number } : {}),
      ...(c.excerpt !== null ? { excerpt: c.excerpt } : {}),
      source_label: c.source_label,
      ...(c.url ? { url: c.url } : {}),
      date: c.date,
      ...(c.locator_label ? { locator_label: c.locator_label } : {}),
      confidential: c.confidential,
    },
  };
}

/** Products with at least one confirmed rating (the server's count per product). */
export function productsWithConfirmed(e: VendorIntelligence): number {
  return e.fit.products.filter((p) => p.confirmed_modules > 0).length;
}

/** The report waits for at least two products with confirmed ratings (E14-design §4). */
export function reportWaitsForRatings(e: VendorIntelligence): boolean {
  return productsWithConfirmed(e) < 2;
}

/**
 * "The top three hold in 2 of 5 checks; 1 only ties", from the server's
 * counts; null before any check applies.
 */
export function sensitivityLine(e: VendorIntelligence): string | null {
  const s = e.fit.sensitivity;
  if (s.checks === 0) return null;
  const n = s.base_top.length;
  const top = n === 3 ? 'top three' : n === 2 ? 'top two' : 'top choice';
  const verb = n === 1 ? 'holds' : 'hold';
  const line = `The ${top} ${verb} in ${s.holds} of ${s.checks} check${s.checks === 1 ? '' : 's'}`;
  return s.tied > 0 ? `${line}; ${s.tied} only tie${s.tied === 1 ? 's' : ''}` : line;
}

/**
 * What a sensitivity case found, in words, from the server's pairs: who
 * ties, who passes whom, who enters or leaves the top three.
 */
export function caseFindings(c: SensitivityCase, nameOf: (id: string) => string): string[] {
  return [
    ...c.tied.map(([a, b]) => `${nameOf(a)} and ${nameOf(b)} tie`),
    ...c.changes.map((ch) =>
      ch.kind === 'passes'
        ? `${nameOf(ch.above)} passes ${nameOf(ch.below)}`
        : ch.kind === 'enters'
          ? `${nameOf(ch.product_id)} enters the top three`
          : `${nameOf(ch.product_id)} leaves the top three`,
    ),
  ];
}

/** "Weight 6 · 2 critical, 1 desired, 1 not set", a module's server figures in words. */
export function moduleMeta(m: VendorIntelligence['modules'][number]): string {
  const parts = [
    m.counts.critical ? `${m.counts.critical} critical` : null,
    m.counts.desired ? `${m.counts.desired} desired` : null,
    m.counts.not_set ? `${m.counts.not_set} not set` : null,
  ].filter((p): p is string => p !== null);
  return `Weight ${m.weight} · ${parts.join(', ')}`;
}

export function plural(n: number, word: string, many = `${word}s`): string {
  return `${n.toLocaleString('en-US')} ${n === 1 ? word : many}`;
}

export function formatDate(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** A product's title, and its vendor only when the vendor's name adds something ("Catalis · Catalis" reads once). */
export function productTitle(p: { name: string; vendor: { name: string } }): {
  title: string;
  vendor: string | null;
} {
  const same = p.name.trim().toLowerCase() === p.vendor.name.trim().toLowerCase();
  return { title: p.name, vendor: same ? null : p.vendor.name };
}

/** Confirmed items plus requirement answers in this engagement's modules. */
export function evidenceOf(p: { confirmed_items: number; answers: number }): number {
  return p.confirmed_items + p.answers;
}

/**
 * All vendors, as the picker lists them: products with evidence in this
 * engagement's modules first, most evidence first, then the ones with none
 * (they can be added, and rate Unknown). Within each, the ones that can be
 * added come before those in scope already or retired; ties by title.
 */
export function pickerSections<
  P extends {
    name: string;
    vendor: { name: string };
    confirmed_items: number;
    answers: number;
    addable: boolean;
    in_scope: boolean;
  },
>(products: readonly P[]): { withEvidence: P[]; noEvidence: P[] } {
  const order = (a: P, b: P) =>
    Number(b.addable && !b.in_scope) - Number(a.addable && !a.in_scope) ||
    evidenceOf(b) - evidenceOf(a) ||
    productTitle(a).title.localeCompare(productTitle(b).title);
  return {
    withEvidence: products.filter((p) => evidenceOf(p) > 0).sort(order),
    noEvidence: products.filter((p) => evidenceOf(p) === 0).sort(order),
  };
}

// ---------------------------------------------------------------------------
// The four steps (T-0272; E24-design §2)
// ---------------------------------------------------------------------------

/** The steps in order, one URL each: `…/vendor-intelligence/<slug>`. */
export const VI_STEPS = [
  { slug: 'vendors', label: 'Vendors' },
  { slug: 'ratings', label: 'Ratings' },
  { slug: 'shortlist', label: 'Shortlist' },
  { slug: 'report', label: 'Report' },
] as const;
export type ViStep = (typeof VI_STEPS)[number]['slug'];

export function viStepFromSlug(raw: unknown): ViStep | null {
  return VI_STEPS.find((s) => s.slug === raw)?.slug ?? null;
}

/** done, locked (waits on an earlier step), or todo; "current" is the URL's. */
export type ViStepState = Exclude<StepState, 'current'>;

/** The Report step waits for the shortlist: every disposition confirmed (T-0274). */
export function reportWaitsForShortlist(e: VendorIntelligence): boolean {
  return !e.fit.dispositions_confirmed;
}

/**
 * Each step's state from the evaluation: Vendors is done with two or more
 * products; Ratings once every cell has a confirmed rating, and waits
 * while there is no product or no evaluated module; Shortlist opens once
 * the fit ranks someone and is done once every disposition is confirmed
 * (the server's `dispositions_confirmed`); Report waits for two products
 * with confirmed ratings and for the confirmed shortlist, and is done once
 * a version is published (a draft alone is not).
 */
export function viStepStates(
  e: VendorIntelligence,
  report?: ReportStatus | null,
): Record<ViStep, ViStepState> {
  const hasProducts = e.products.length > 0;
  const hasModules = e.modules.length > 0;
  const ratingsDone =
    hasProducts && hasModules && e.counts.cells > 0 && e.counts.confirmed === e.counts.cells;
  const shortlistDone = e.fit.dispositions_confirmed;
  const published = (report?.versions.length ?? 0) > 0;
  return {
    vendors: e.products.length >= 2 ? 'done' : 'todo',
    ratings: ratingsDone ? 'done' : hasProducts && hasModules ? 'todo' : 'locked',
    shortlist: shortlistDone ? 'done' : e.fit.top.length > 0 ? 'todo' : 'locked',
    report: published
      ? 'done'
      : reportWaitsForRatings(e) || reportWaitsForShortlist(e)
        ? 'locked'
        : 'todo',
  };
}

/** Where the bare URL lands: the first step not done, the last when every one is. */
export function defaultViStep(e: VendorIntelligence, report?: ReportStatus | null): ViStep {
  const states = viStepStates(e, report);
  return VI_STEPS.find((s) => states[s.slug] !== 'done')?.slug ?? 'report';
}

/** What the status line needs of the report read; absent while it loads. */
export interface ReportStatus {
  draft: { version: number } | null;
  versions: ReadonlyArray<{ version: number }>;
}

/**
 * "6 vendors · 86 of 96 ratings confirmed · 3 shortlisted · Draft v2",
 * every count the server's. Until the shortlist is confirmed the third
 * segment reads the fit's top three ("2 in the top three"), code's
 * proposal; once confirmed, the confirmed shortlist. The last segment
 * names the published version, the draft, or both ("Draft v3 · v2
 * published").
 */
export function viStatusLine(e: VendorIntelligence, report?: ReportStatus | null): string {
  const n = (v: number) => v.toLocaleString('en-US');
  const parts = [plural(e.products.length, 'vendor')];
  parts.push(
    e.counts.cells > 0
      ? `${n(e.counts.confirmed)} of ${n(e.counts.cells)} ratings confirmed`
      : 'no ratings yet',
  );
  if (e.fit.dispositions_confirmed) {
    parts.push(`${n(shortlistedProducts(e).length)} shortlisted`);
  } else if (e.fit.top.length > 0) {
    parts.push(`${n(e.fit.top.length)} in the top three`);
  }
  if (report) {
    const latest = report.versions.reduce((max, v) => Math.max(max, v.version), 0);
    parts.push(
      report.draft
        ? latest > 0
          ? `Draft v${report.draft.version} · v${latest} published`
          : `Draft v${report.draft.version}`
        : latest > 0
          ? `Published v${latest}`
          : 'No report yet',
    );
  }
  return parts.join(' · ');
}

/** "N requirements in M client areas are not mapped to a module and are not rated"; null when all are mapped. */
export function notMappedNote(data: NotAssessed | null | undefined): string | null {
  if (!data || data.requirements === 0) return null;
  const areas = data.areas.length;
  return `${plural(data.requirements, 'requirement')} in ${plural(areas, 'client area')} ${
    data.requirements === 1 ? 'is' : 'are'
  } not mapped to a module and ${data.requirements === 1 ? 'is' : 'are'} not rated`;
}

// ---------------------------------------------------------------------------
// The shortlist shell (T-0272; E24-design §3): rank order and the checks
// ---------------------------------------------------------------------------

/** The fit's products in base rank order: ranked first, then the unranked as they came. */
export function rankedProducts(e: VendorIntelligence): ProductFit[] {
  return [...e.fit.products].sort(
    (a, b) => (a.rank ?? Number.MAX_SAFE_INTEGER) - (b.rank ?? Number.MAX_SAFE_INTEGER),
  );
}

/** "1", "2=" for a shared rank; null while unranked. */
export function rankLabel(p: Pick<ProductFit, 'rank' | 'tied_with'>): string | null {
  if (p.rank === null) return null;
  return p.tied_with.length > 0 ? `${p.rank}=` : String(p.rank);
}

/** "#2 · 56.9": a vendor's rank and index, under its name in the grid; null while unranked. */
export function fitLine(p: Pick<ProductFit, 'rank' | 'tied_with' | 'index'>): string | null {
  const rank = rankLabel(p);
  return rank === null ? null : `#${rank} · ${p.index.toFixed(1)}`;
}

/** The five checks in plain words, by the deriver's key; the server's label otherwise. */
export const SENSITIVITY_LABEL: Record<string, string> = {
  equal_weights: 'Every area weighted equally',
  critical_only: 'Critical requirements only',
  unknown_quarter: 'If unknown counted a quarter',
  partial_quarter: 'If partial counted less',
  partial_three_quarters: 'If partial counted more',
};

export function sensitivityLabel(c: Pick<SensitivityCase, 'key' | 'label'>): string {
  return SENSITIVITY_LABEL[c.key] ?? c.label;
}

/** A check's outcome as a chip: a tie is a tie, never shown as a change of order. */
export const OUTCOME_LABEL: Record<SensitivityCase['outcome'], string> = {
  holds: 'Holds',
  tied: 'Ties',
  changes: 'Order changes',
  not_applicable: 'Not applicable',
};

/** The legend of the answer codes a vendor's requirement responses carry. */
export const ANSWER_LEGEND =
  'S Standard · C Configured · T Third party · F Future · N Not supported';

// ---------------------------------------------------------------------------
// Dispositions (T-0274; E24-design §3 item 3; ADR-026 decision 3)
// ---------------------------------------------------------------------------

/** The step routes; the bare URL (`step` null) lands on the first step not done. */
export const VI_STEP_PATH = '/engagements/$engagementId/selection/vendor-intelligence/$step';
export const VI_INDEX_PATH = '/engagements/$engagementId/selection/vendor-intelligence';

export const DISPOSITION_LABEL: Record<Disposition, string> = {
  shortlisted: 'Shortlisted',
  alternate: 'Alternate',
  set_aside: 'Set aside',
  not_assessed: 'Not assessed',
};

/**
 * What a consultant chooses between for a rated vendor. Not assessed is
 * code's word for a vendor with no confirmed rating; it is shown, not
 * chosen.
 */
export const DISPOSITION_CHOICES = ['shortlisted', 'alternate', 'set_aside'] as const;

/** The server's minimum (`MIN_DISPOSITION_REASON_CHARS` in the API). */
export const MIN_DISPOSITION_REASON_CHARS = 10;

export interface DispositionRowView {
  product: ProductInScope;
  /** The fit's figures for the product; null before the fit knows it. */
  fit: ProductFit | null;
  /** "1", "2=" for a shared rank; null while unranked. */
  rank: string | null;
}

/** One row per product in scope, in the fit's rank order: ranked first, then the unranked as they came. */
export function dispositionRows(e: VendorIntelligence): DispositionRowView[] {
  const fitOf = new Map(e.fit.products.map((p) => [p.product_id, p]));
  const at = new Map(rankedProducts(e).map((p, i) => [p.product_id, i]));
  const order = (id: string) => at.get(id) ?? Number.MAX_SAFE_INTEGER;
  return [...e.products]
    .sort((a, b) => order(a.product_id) - order(b.product_id))
    .map((product) => {
      const fit = fitOf.get(product.product_id) ?? null;
      return { product, fit, rank: fit ? rankLabel(fit) : null };
    });
}

/** What "Confirm shortlist" sends: every proposed row as proposed, no reason. */
export function proposedDispositionRows(e: VendorIntelligence): DispositionRow[] {
  return dispositionRows(e).flatMap((r) =>
    r.product.disposition_status === 'proposed' && r.product.disposition !== null
      ? [{ product_id: r.product.product_id, disposition: r.product.disposition }]
      : [],
  );
}

/** A change from what the row holds needs a reason; a row never proposed counts as a change. */
export function reasonNeeded(product: ProductInScope, choice: Disposition): boolean {
  return product.disposition !== choice;
}

/** The reason as the server normalises it: single spaces, trimmed. */
export function normaliseReason(reason: string): string {
  return reason.replace(/\s+/g, ' ').trim();
}

export function shortlistedProducts(e: VendorIntelligence): ProductInScope[] {
  return e.products.filter((p) => p.disposition === 'shortlisted');
}

/** "2 shortlisted, 1 alternate, 1 set aside": what a set of rows settles. */
export function dispositionMixText(rows: ReadonlyArray<{ disposition: Disposition }>): string {
  const order: Disposition[] = ['shortlisted', 'alternate', 'set_aside', 'not_assessed'];
  const many: Record<Disposition, [string, string]> = {
    shortlisted: ['shortlisted', 'shortlisted'],
    alternate: ['alternate', 'alternates'],
    set_aside: ['set aside', 'set aside'],
    not_assessed: ['not assessed', 'not assessed'],
  };
  return order
    .map((d) => [d, rows.filter((r) => r.disposition === d).length] as const)
    .filter(([, n]) => n > 0)
    .map(([d, n]) => `${n.toLocaleString('en-US')} ${n === 1 ? many[d][0] : many[d][1]}`)
    .join(', ');
}

// ---------------------------------------------------------------------------
// Fit by client area (T-0276; E24-design §4; ADR-026 decision 4)
// ---------------------------------------------------------------------------

/**
 * The vendors assessed in depth, the matrix's columns: the shortlisted and
 * alternate products in rank order. While code has proposed nothing yet
 * (rows from before dispositions existed), every ranked vendor.
 */
export function assessedInDepth(e: VendorIntelligence): ProductFit[] {
  const ranked = rankedProducts(e).filter((p) => p.rank !== null);
  if (!e.products.some((p) => p.disposition !== null)) return ranked;
  const inDepth = new Set(
    e.products
      .filter((p) => p.disposition === 'shortlisted' || p.disposition === 'alternate')
      .map((p) => p.product_id),
  );
  return ranked.filter((p) => inDepth.has(p.product_id));
}

/** "2 critical, 1 desired · 3 not mapped": an area's server counts in words. */
export function areaMeta(a: {
  counts: { critical: number; desired: number; not_set: number };
  unmapped: number;
}): string {
  const parts = [
    a.counts.critical ? `${a.counts.critical} critical` : null,
    a.counts.desired ? `${a.counts.desired} desired` : null,
    a.counts.not_set ? `${a.counts.not_set} not set` : null,
  ].filter((p): p is string => p !== null);
  const mapped = parts.join(', ');
  return a.unmapped > 0 ? `${mapped}${mapped ? ' · ' : ''}${a.unmapped} not mapped` : mapped;
}

/** "66.7%": the share of the weight with no evidence behind it, from the server's coverage. */
export function weightWithNoEvidence(p: Pick<ProductFit, 'coverage'>): string {
  return `${(100 - p.coverage).toFixed(1)}%`;
}

/** "25%": a 0..1 share as a whole percentage. */
export function sharePercent(share: number): string {
  return `${Math.round(share * 100)}%`;
}
